import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { mediaUrl } from "@/lib/mediaUrl";

export const LANG_ORDER = ["ar", "fr", "en"];

export const LANG_META = {
  ar: { flag: mediaUrl("/flags/ar.png"), names: { ar: "العربية", fr: "Arabe", en: "Arabic" } },
  fr: { flag: mediaUrl("/flags/fr.png"), names: { ar: "الفرنسية", fr: "Français", en: "French" } },
  en: { flag: mediaUrl("/flags/en.png"), names: { ar: "الإنجليزية", fr: "Anglais", en: "English" } },
};

const LanguageSwitcher = ({ className = "" }) => {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative z-10 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="language-switcher-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        className="flex h-9 cursor-pointer items-center gap-1.5 rounded-full border border-line bg-white pe-2 ps-2.5 outline-none transition-colors duration-200 hover:border-sage focus-visible:ring-2 focus-visible:ring-sage"
      >
        <img src={LANG_META[lang].flag} alt="" className="size-5 rounded-full object-cover" />
        <ChevronDown
          className={`size-3.5 text-graphite/60 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.97 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            role="listbox"
            data-testid="language-menu"
            className="absolute end-0 top-[calc(100%+0.55rem)] z-[70] w-44 overflow-hidden rounded-2xl border border-line bg-white p-1.5 shadow-[0_24px_60px_-24px_rgba(11,11,11,0.32)]"
          >
            {LANG_ORDER.map((code) => {
              const current = code === lang;
              return (
                <button
                  key={code}
                  type="button"
                  role="option"
                  aria-selected={current}
                  onClick={() => {
                    setLang(code);
                    setOpen(false);
                  }}
                  data-testid={`language-option-${code}`}
                  className={`flex w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-sage ${
                    current ? "bg-mist/60 text-charcoal" : "text-graphite/80 hover:bg-mist/40"
                  }`}
                >
                  <img src={LANG_META[code].flag} alt="" className="size-5 rounded-full object-cover" />
                  <span className="flex-1 text-start">{LANG_META[code].names[lang]}</span>
                  {current && <Check className="size-4 text-sage-deep" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
